import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import Lock from 'assets/svgs/lock.svg';
import { ONLINE_REGS_STATUS_INITIALS, ONLINE_REGS_STATUSES, WKYS_EXAMINEE_TYPES } from 'shared/constants';
import { ModalInfo, Tab, AlertNotice, Tabs, ScrollFade, Table, Textarea } from 'shared/components';
import { useDispatch } from 'react-redux';
import OnlineTestingService from '../OnlineTestingService';
import * as onlineTestingActions from '../duck';
import './BulkChangeStatusModal.scss';

const TABS = {
  resume: 1,
  markComplete: 2,
};
const DEFAULT_TAB = TABS.resume;
const ROW_HEADERS = ['name', 'status', 'examType'];

const checkPermissionChangeStatus = (reg, nextStatus) => {
  switch (nextStatus) {
    case ONLINE_REGS_STATUS_INITIALS.resumed:
      return (
        reg.testStatus === ONLINE_REGS_STATUS_INITIALS.exited ||
        reg.testStatus === ONLINE_REGS_STATUS_INITIALS.active
      );
    case ONLINE_REGS_STATUS_INITIALS.markedComplete:
      return (
        reg.testStatus === ONLINE_REGS_STATUS_INITIALS.exited ||
        reg.testStatus === ONLINE_REGS_STATUS_INITIALS.resumed ||
        reg.testStatus === ONLINE_REGS_STATUS_INITIALS.active
      );
    default:
      break;
  }
};

const mapToList = (regs, listOfSelectedExaminees, nextStatus) =>
  regs.map((reg) => ({
    name: <div className="fw-bold">{reg.lastName.trim().concat(', ', reg.firstName.trim())}</div>,
    status: (
      <div className={`status-cell ${ONLINE_REGS_STATUSES[reg.testStatus]}`}>
        {reg.locked && <Lock />}
        <span className="title-label">
          {ONLINE_REGS_STATUSES[reg.testStatus] || ONLINE_REGS_STATUSES.notReady}
        </span>
      </div>
    ),
    examType: reg.capacityType.name,
    id: reg.id,
    checked: listOfSelectedExaminees.some((el) => el.id === reg.id) || false,
    forbiddenSelect: !checkPermissionChangeStatus(reg, nextStatus),
  }));

const BulkChangeStatusModal = ({
  handleClose,
  isNCRCRoom,
  isOpen,
  listOfExaminees,
  regTypes,
  roomId,
  scrollPosition,
  selectedAssessmentTab,
  setScrollPosition,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const [reason, setReason] = useState('');
  const [resumeList, setResumeList] = useState(listOfExaminees);
  const [markCompleteList, setMarkCompleteList] = useState(listOfExaminees);
  const [errorReason, setErrorReason] = useState('');

  const TABLE_HEADERS = {
    name: t('common.labels.name'),
    status: t('onlineTesting.listView.tableHeaders.status'),
    examType: t('onlineTesting.listView.tableHeaders.examType'),
  };

  useEffect(() => {
    if (isOpen) {
      setResumeList(
        listOfExaminees.filter((el) =>
          checkPermissionChangeStatus(el, ONLINE_REGS_STATUS_INITIALS.resumed)
        )
      );
      setMarkCompleteList(
        listOfExaminees.filter((el) =>
          checkPermissionChangeStatus(el, ONLINE_REGS_STATUS_INITIALS.markedComplete)
        )
      );
    }
  }, [listOfExaminees, isOpen]);

  const onClickTab = (tab) => {
    setActiveTab(tab);
  };

  const onChangeText = (e) => {
    setReason(e.target.value);
    setErrorReason('');
  };

  const selectExaminee = (e) => {
    const { checked, value } = e.target;
    let clonedSelected;

    const updateList = (clonedSelected) => {
      const idx = listOfExaminees.findIndex((exam) => exam.id === +value);
      if (checked) {
        clonedSelected.push(listOfExaminees[idx]);
      } else {
        clonedSelected.splice(
          clonedSelected.findIndex((el) => el.id === +value),
          1
        );
      }
      return clonedSelected;
    };

    switch (activeTab) {
      case TABS.resume: {
        clonedSelected = cloneDeep(resumeList);
        setResumeList(updateList(clonedSelected));
        break;
      }
      case TABS.markComplete: {
        clonedSelected = cloneDeep(markCompleteList);
        setMarkCompleteList(updateList(clonedSelected));
        break;
      }
      default:
        break;
    }
  };

  const onModalClose = (clearListFlag) => {
    setErrorReason('');
    setReason('');
    handleClose(clearListFlag);
    setActiveTab(DEFAULT_TAB);
  };

  const handleUpdateStatus = async (status, list, reason) => {
    if (list.length) {
      await OnlineTestingService.putStudentStatus(
        list.map((reg) => ({
          regId: reg.id,
          testStatus: status,
          proctorTerminate: reason || '',
        }))
      );
      
      // For NCRC rooms, passing regTypeId to fetch filtered data
      let regTypeId = null;
      if (isNCRCRoom && regTypes?.length > 0) {
        const assessmentCodes = Object.entries(WKYS_EXAMINEE_TYPES)
          .filter(([code]) => code !== 'ncrc')
          .map(([code]) => code);
        const assessment = assessmentCodes[selectedAssessmentTab - 1];
        const regType = regTypes.find((rt) => rt.code === assessment?.toUpperCase());
        regTypeId = regType?.id || null;
      }
      
      dispatch(onlineTestingActions.getOnlineRegs(roomId, regTypeId));
      onModalClose(true);
    }
  };

  const handleSubmit = () => {
    if (activeTab === TABS.markComplete && !reason) {
      setErrorReason(t('errors.fieldRequired'));
      return;
    }

    switch (activeTab) {
      case TABS.resume: {
        handleUpdateStatus(ONLINE_REGS_STATUS_INITIALS.resumed, resumeList);
        break;
      }
      case TABS.markComplete: {
        handleUpdateStatus(ONLINE_REGS_STATUS_INITIALS.markedComplete, markCompleteList, reason);
        break;
      }
      default:
        break;
    }
  };

  const checkListSelectionAvailability = (status) =>
    listOfExaminees.some((el) => checkPermissionChangeStatus(el, status));

  const getTooltipContent = () => {
    switch (activeTab) {
      case TABS.resume: {
        return !resumeList.length &&
          checkListSelectionAvailability(ONLINE_REGS_STATUS_INITIALS.resumed)
          ? t('onlineTesting.statusModal.disabledNoSelection')
          : t('onlineTesting.statusModal.disabledInvalidTooltip');
      }
      case TABS.markComplete: {
        return !markCompleteList.length &&
          checkListSelectionAvailability(ONLINE_REGS_STATUS_INITIALS.markedComplete)
          ? t('onlineTesting.statusModal.disabledNoSelection')
          : t('onlineTesting.statusModal.disabledInvalidTooltip');
      }
      default:
        break;
    }
  };

  return (
    <ModalInfo
      className="change-status-modal"
      modalIsOpen={isOpen}
      closeModal={() => onModalClose()}
      onSubmit={handleSubmit}
      headerTitle={t('onlineTesting.statusModal.title')}
      saveLabel={
        activeTab === TABS.markComplete
          ? t('onlineTesting.statusModal.markComplete.tabLabel')
          : t('onlineTesting.statusModal.resume.tabLabel')
      }
      cancelLabel={t('common.labels.back')}
      disabledSubmit={
        (activeTab === TABS.markComplete && !markCompleteList.length) ||
        (activeTab === TABS.resume && !resumeList.length)
      }
      tooltipContent={getTooltipContent()}
      headerBack
      showCancel
      wideModal>
      <div>
        <Tabs defaultTab={DEFAULT_TAB} onClickAction={onClickTab}>
          <Tab title={t('onlineTesting.statusModal.resume.tabLabel')}>
            <div>
              <h4>{t('onlineTesting.statusModal.resume.title')}</h4>
              <AlertNotice message={t('onlineTesting.statusModal.resume.warning')} type="warning" />
              {!resumeList.length &&
                checkListSelectionAvailability(ONLINE_REGS_STATUS_INITIALS.resumed) && (
                  <AlertNotice
                    message={t('onlineTesting.statusModal.disabledNoSelection')}
                    type="error"
                  />
                )}
            </div>
            <ScrollFade setScrollPosition={setScrollPosition} scrollPosition={scrollPosition}>
              <Table
                onSelectRow={selectExaminee}
                rowElements={ROW_HEADERS}
                tableHeaders={TABLE_HEADERS}
                listOfRegs={mapToList(
                  listOfExaminees,
                  resumeList,
                  ONLINE_REGS_STATUS_INITIALS.resumed
                )}
                tableName="resume-table"
                tooltipForbidden={t('onlineTesting.statusModal.tooltipForbidden')}
                selectOptionEnabled></Table>
            </ScrollFade>
          </Tab>
          <Tab
            title={t('onlineTesting.statusModal.markComplete.tabLabel')}
            className="tab-mark-complete">
            <div>
              <h4>{t('onlineTesting.statusModal.markComplete.title')}</h4>
              <AlertNotice
                message={t('onlineTesting.statusModal.markComplete.warning')}
                type="warning"
              />
              {!markCompleteList.length &&
                checkListSelectionAvailability(ONLINE_REGS_STATUS_INITIALS.markedComplete) && (
                  <AlertNotice
                    message={t('onlineTesting.statusModal.disabledNoSelection')}
                    type="error"
                  />
                )}
              <Textarea
                label={t('onlineTesting.individualStatus.completeReason')}
                labelInfo={t('onlineTesting.individualStatus.markingReason')}
                name="completeReason"
                value={reason}
                onChange={onChangeText}
                maxLength={500}
                error={errorReason}
              />
            </div>
            <ScrollFade setScrollPosition={setScrollPosition} scrollPosition={scrollPosition}>
              <Table
                onSelectRow={selectExaminee}
                rowElements={ROW_HEADERS}
                tableHeaders={TABLE_HEADERS}
                listOfRegs={mapToList(
                  listOfExaminees,
                  markCompleteList,
                  ONLINE_REGS_STATUS_INITIALS.markedComplete
                )}
                tableName="mark-complete-list"
                tooltipForbidden={t('onlineTesting.statusModal.tooltipForbidden')}
                selectOptionEnabled></Table>
            </ScrollFade>
          </Tab>
        </Tabs>
      </div>
    </ModalInfo>
  );
};

export default BulkChangeStatusModal;
